import cloneDeep from 'lodash/cloneDeep';
import { postEasyblocksItemInserted } from './post-message';
import { addSwellPagePrefix } from './easyblocks';

const EXCLUDED_SECTION_IDS = Object.freeze(['apps']);
const EXCLUDED_BLOCK_TYPES = Object.freeze(['@app']);

function getTemplateData(easyblocksConfig, template) {
  const { components, types } = easyblocksConfig;
  const component = components.find(
    (component) => component.id === template._component,
  );

  return component.schema.reduce((acc, schema) => {
    const { prop, type } = schema;
    const value = acc[prop];

    if (type !== 'component-collection') {
      const widgetId = types[type].widget.id;
      const hasValue = value !== null && value !== undefined;

      acc[prop] = {
        value: hasValue ? value : '',
        widgetId,
      };
    } else {
      const hasValue = value?.length > 0;

      acc[prop] = hasValue
        ? value.map((item) => getTemplateData(easyblocksConfig, item))
        : [];
    }

    return acc;
  }, cloneDeep(template));
}

function getItemData(easyblocksConfig, templateId) {
  const { templates } = easyblocksConfig;
  const template = templates.find((template) => template.id === templateId);

  if (!template) {
    return;
  }

  return getTemplateData(easyblocksConfig, template.entry);
}

function calculateBlocks(section, blockId) {
  const { Blocks: blocks } = section;
  const hasBlocks = blocks?.length > 0;

  if (!hasBlocks) {
    return 0;
  }

  return blocks.reduce(
    (acc, block) => acc + (block._component === blockId ? 1 : 0),
    0,
  );
}

export function addPageItem(easyblocksConfig, templateId, name, index) {
  const itemData = getItemData(easyblocksConfig, templateId);

  if (!itemData) {
    return;
  }

  const path = addSwellPagePrefix(name);

  postEasyblocksItemInserted(path, itemData, index);
}

export function getSectionsToAdd(editorConfig, easyblocksConfig, path) {
  const { allSections } = editorConfig;
  const { templates } = easyblocksConfig;

  const items = allSections.reduce((acc, section) => {
    if (EXCLUDED_SECTION_IDS.includes(section.id)) {
      return acc;
    }

    // TODO: Add support for multiple presets
    const sectionTemplateId = `${section.id}__preset_0`;
    const template = templates.find(
      (template) => template.id === sectionTemplateId,
    );
    const hasTemplate = Boolean(template);
    const hasPresets = section.presets?.length > 0;
    const disabledGroups = section.disabled_on?.groups;
    const enabledGroups = section.enabled_on?.groups;
    const hasDisabledGroups = disabledGroups?.length > 0;
    const hasEnabledGroups = enabledGroups?.length > 0;
    const isDisabled = hasDisabledGroups
      ? disabledGroups.some((group) => path.includes(group))
      : false;
    const isEnabled = hasEnabledGroups
      ? enabledGroups.some((group) => path.includes(group))
      : true;
    const canAddSection = hasTemplate && hasPresets && !isDisabled && isEnabled;

    if (canAddSection) {
      acc.push({
        id: sectionTemplateId,
        type: section.id,
        label: section.label,
      });
    }

    return acc;
  }, []);

  items.sort((a, b) => a.label.localeCompare(b.label));

  return items;
}

export function getBlocksToAdd(editorConfig, easyblocksConfig, document, path) {
  const { allSections } = editorConfig;
  const { templates } = easyblocksConfig;
  const [sectionGroup, sectionIndex] = path.split('.');
  const section = document[sectionGroup][sectionIndex];
  const sectionConfig = allSections.find(
    (sectionConfig) => sectionConfig.id === section._component,
  );

  const result = (sectionConfig.blocks || []).reduce(
    (acc, block) => {
      const { type, label, limit } = block;
      const hasLabel = Boolean(label);

      if (!hasLabel || EXCLUDED_BLOCK_TYPES.includes(type)) {
        return acc;
      }

      const id = `Block__${section._component}__${type}`;
      const template = templates.find((template) => template.id === id);
      const hasTemplate = Boolean(template);

      if (!hasTemplate) {
        return acc;
      }

      const hasLimit = typeof limit === 'number';
      const isLimitReached = hasLimit && calculateBlocks(section, id) >= limit;

      if (isLimitReached) {
        acc.isLimitReached = true;
        acc.limit += limit;

        return acc;
      }

      acc.blocks.push({ id, label, type });

      return acc;
    },
    { blocks: [], isLimitReached: false, limit: 0 },
  );

  const hasBlocks = result.blocks.length > 0;

  if (!hasBlocks) {
    return result;
  }

  return {
    blocks: result.blocks.sort((a, b) => a.label.localeCompare(b.label)),
    isLimitReached: false,
    limit: result.limit,
  };
}
